import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { useAuthStore, refreshProfile } from './stores/authStore.js';
import { AppShell } from './components/shell/AppShell.js';
import { RouteErrorBoundary } from './components/shell/RouteErrorBoundary.js';
import { isAnyDialogOpen, ToastProvider, useToast } from './components/ui/index.js';

// Real page components
import { LandingPage } from './pages/LandingPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LibraryPage } from './pages/LibraryPage.js';
import { TunerPage } from './pages/TunerPage.js';
import { MarketplacePage } from './pages/MarketplacePage.js';
import { AuthPage } from './pages/AuthPage.js';
import { KitGalleryPage } from './pages/KitGalleryPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { MyEditsPage } from './pages/MyEditsPage.js';
import { useSyncStore } from './stores/syncStore.js';
import { ROUTINE_RESTORED_EVENT } from './sync/routineSync.js';
import { VerifyEmailDialog } from './components/auth/VerifyEmailDialog.js';
import { ChangeEmailDialog } from './components/auth/ChangeEmailDialog.js';

import { db } from './db/index.js';

// Heavy routes are code-split so alphaTab is not downloaded on the dashboard
const importTabViewer = () => import('./pages/TabViewerPage.js');
const importRoutineArchitect = () => import('./pages/RoutineArchitectPage.js');
const importFocusHud = () => import('./pages/FocusHudPage.js');
const importAdminModeration = () => import('./pages/AdminModerationPage.js');
const lazyPage = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, name: K) =>
  React.lazy(() => load().then((m) => ({ default: m[name] })));
const ChordListPage = lazyPage(() => import('./pages/ChordListPage.js'), 'ChordListPage');
const ChordDetailPage = lazyPage(() => import('./pages/ChordDetailPage.js'), 'ChordDetailPage');
const ScaleListPage = lazyPage(() => import('./pages/ScaleListPage.js'), 'ScaleListPage');
const ScaleDetailPage = lazyPage(() => import('./pages/ScaleDetailPage.js'), 'ScaleDetailPage');
const ChordPracticePage = lazyPage(() => import('./pages/ChordPracticePage.js'), 'ChordPracticePage');
const ScalePracticePage = lazyPage(() => import('./pages/ScalePracticePage.js'), 'ScalePracticePage');
const TabViewerPage = React.lazy(() => importTabViewer().then((m) => ({ default: m.TabViewerPage })));
const RoutineArchitectPage = React.lazy(() => importRoutineArchitect().then((m) => ({ default: m.RoutineArchitectPage })));
const FocusHudPage = React.lazy(() => importFocusHud().then((m) => ({ default: m.FocusHudPage })));
const AdminModerationPage = React.lazy(() => importAdminModeration().then((m) => ({ default: m.AdminModerationPage })));

// Offline-first: prefetch the lazy route chunks so the service worker caches them and every route
// works if the user goes offline before ever visiting it.
//  - Routine architect + Focus HUD chunks do not pull alphaTab, so they are fetched right after mount.
//  - Tab viewer (alphaTab) and admin chunks are fetched only after a delay + idle, so the dashboard's
//    initial load stays free of alphaTab (e2e 159 observes the first second).
const HEAVY_PREFETCH_DELAY_MS = 3000;
function prefetchRouteChunks() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  [importRoutineArchitect, importFocusHud].forEach((load) => {
    load().catch(() => {});
  });
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  setTimeout(() => {
    const run = () => {
      [importTabViewer, importAdminModeration].forEach((load) => {
        load().catch(() => {});
      });
    };
    if (ric) ric(run);
    else run();
  }, HEAVY_PREFETCH_DELAY_MS);
}

const RouteFallback: React.FC = () => (
  <div className="min-h-full flex items-center justify-center p-8" role="status" aria-live="polite">
    <span className="loading loading-spinner loading-lg text-primary" aria-hidden="true" />
    <span className="sr-only">در حال بارگذاری</span>
  </div>
);

/** FND-08: top-level boundary keyed by pathname so navigating away clears a failed-chunk error. */
const LocationErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.pathname} fullScreen>
      {children}
    </RouteErrorBoundary>
  );
};

/** FRE-137 / FE-44: global verify dialog + refresh profile from /me on mount, token change, and online event. */
const VerifyEmailGate: React.FC = () => {
  const token = useAuthStore((s) => s.token);
  const isGuest = useAuthStore((s) => s.isGuest);
  const toast = useToast();

  // FE-44: /me can demote the user at any moment (e.g. AdminRoute unmounts the page before it can
  // react), so announce it here once, whichever code path applied the new role.
  React.useEffect(() => {
    return useAuthStore.subscribe((next, prev) => {
      if (prev.user?.role === 'ADMIN' && next.user?.role === 'USER' && next.user.id === prev.user.id) {
        toast.error('دسترسی مدیریت شما برداشته شده است.');
      }
    });
  }, [toast]);

  React.useEffect(() => {
    if (!token || isGuest) return;
    void refreshProfile();

    const handleOnline = () => {
      void refreshProfile();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [token, isGuest]);

  return (
    <>
      <VerifyEmailDialog />
      <ChangeEmailDialog />
    </>
  );
};

const SyncAppWatcher: React.FC = () => {
  const toast = useToast();
  const { initSyncStore, deferredPrompt } = useSyncStore();
  const isOfflineRef = React.useRef(typeof navigator !== 'undefined' && !navigator.onLine);

  React.useEffect(() => {
    const cleanup = initSyncStore();

    const handleOfflineToast = () => {
      if (isOfflineRef.current) return;
      isOfflineRef.current = true;
      toast.info('حالت آفلاین فعال شد. تغییرات روی دستگاه ذخیره می‌شوند.');
    };

    const handleOnlineReset = () => {
      isOfflineRef.current = false;
    };

    window.addEventListener('offline', handleOfflineToast);
    window.addEventListener('online', handleOnlineReset);
    return () => {
      cleanup();
      window.removeEventListener('offline', handleOfflineToast);
      window.removeEventListener('online', handleOnlineReset);
    };
  }, [initSyncStore, toast]);

  // FRE-148: the server resurrected a soft-deleted routine on push.
  React.useEffect(() => {
    const onRestored = (e: Event) => {
      const title = (e as CustomEvent<{ title?: string }>).detail?.title;
      toast.info(title ? `روتین «${title}» بازیابی شد.` : 'روتین بازیابی شد.');
    };
    window.addEventListener(ROUTINE_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(ROUTINE_RESTORED_EVENT, onRestored);
  }, [toast]);

  // Polite toast on qualifying visit when PWA prompt is captured
  React.useEffect(() => {
    if (!deferredPrompt) return;
    const hasPrompted = localStorage.getItem('fretboard_pwa_toast_shown');
    if (!hasPrompted) {
      localStorage.setItem('fretboard_pwa_toast_shown', 'true');
      toast.info('امکان نصب وب‌اپلیکیشن FretBoard برای دسترسی آفلاین', {
        label: 'نصب برنامه',
        onClick: async () => {
          try {
            await deferredPrompt.prompt();
            await deferredPrompt.userChoice;
          } catch {
            // ignore
          }
        }
      });
    }
  }, [deferredPrompt, toast]);

  return null;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location.pathname + location.search }} />;
  }
  if (user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const RootRoute: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

const AndroidBackButtonHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    let removeCapListener: (() => void) | undefined;
    try {
      const listenerPromise = CapApp.addListener('backButton', () => {
        if (isAnyDialogOpen()) {
          // Open dialog/sheet handles closing itself before any route navigation occurs
          return;
        }

        const rootPaths = ['/', '/dashboard', '/landing', '/auth'];
        if (!rootPaths.includes(location.pathname)) {
          navigate(-1);
        } else {
          CapApp.exitApp();
        }
      });
      listenerPromise
        .then((handle) => {
          removeCapListener = () => handle.remove();
        })
        .catch(() => {});
    } catch {
      // Not on native device, ignore
    }

    return () => {
      removeCapListener?.();
    };
  }, [location.pathname, navigate]);

  return null;
};

export const App: React.FC = () => {
  React.useEffect(() => {
    db.open().catch((err) => console.error('Dexie DB initialization failed:', err));
    prefetchRouteChunks();
  }, []);

  return (
    <BrowserRouter>
      <AndroidBackButtonHandler />
      <ToastProvider>
        <SyncAppWatcher />
        <VerifyEmailGate />
        <LocationErrorBoundary>
        <React.Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Dev-only Kit Gallery route */}
          {import.meta.env.DEV && (
            <Route path="/__kit" element={<KitGalleryPage />} />
          )}

          {/* Public Landing & Auth Routes (Rendered outside AppShell) */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />

          {/* Shell-less Player and HUD routes */}
          <Route
            path="/tabs/:id"
            element={
              <ProtectedRoute>
                <TabViewerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tab/:id"
            element={
              <ProtectedRoute>
                <TabViewerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/routines/:id/hud"
            element={
              <ProtectedRoute>
                <FocusHudPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/practice/chords"
            element={
              <ProtectedRoute>
                <ChordPracticePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice/scale"
            element={
              <ProtectedRoute>
                <ScalePracticePage />
              </ProtectedRoute>
            }
          />

          {/* Studio Workspace Routes (Wrapped with AppShell) */}
          <Route element={<AppShell />}>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tabs"
              element={
                <ProtectedRoute>
                  <LibraryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/library"
              element={<Navigate to="/tabs" replace />}
            />
            <Route
              path="/chords"
              element={
                <ProtectedRoute>
                  <ChordListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chords/:instrument/:chordId"
              element={
                <ProtectedRoute>
                  <ChordDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scales"
              element={
                <ProtectedRoute>
                  <ScaleListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scales/:instrument/:scaleId"
              element={
                <ProtectedRoute>
                  <ScaleDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/routines"
              element={
                <ProtectedRoute>
                  <RoutineArchitectPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tuner"
              element={
                <ProtectedRoute>
                  <TunerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <MarketplacePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-edits"
              element={
                <ProtectedRoute>
                  <MyEditsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/moderation"
              element={
                <AdminRoute>
                  <AdminModerationPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/*"
              element={
                <AdminRoute>
                  <AdminModerationPage />
                </AdminRoute>
              }
            />
          </Route>

          {/* Fallback 404 Route (FRE-199) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </React.Suspense>
        </LocationErrorBoundary>
      </ToastProvider>
    </BrowserRouter>
  );
};

export default App;
